import { mapState, mapGetters } from 'vuex'
import { deduplicateArrayByKey } from '@/utils'

export const HardwareConfig = {
  data() {
    return {
      query: {
        ram: '',
        model: [],
        gpuNum: '',
      },
      hardwareConfigCandidates: [],
      hardwareConfigList: [],
      hardwareConfigId: -1,
      hardwareConfigIds: [],
      selectTaskList: [],
      currentConfig: {
        model: [],
        gpuNum: '',
        ram: '',
        status: [],
      },
    }
  },
  computed: {
    ...mapState('home', ['platform']),
    ...mapGetters('home', ['isGpuPlatform']),
    _isGpuPlatform() {
      return this.isGpuPlatform || this.currentConfig.gpuNum
    },
    modelLabel() {
      return this._isGpuPlatform ? this.$t('task_gpu_model') : this.$t('task_cpu_model')
    },
    hardwareConfigCandidateList() {
      return this.hardwareConfigCandidates.filter((i) => this.query.model.includes(i.model))
    },
    ramList() {
      const commonList = this.hardwareConfigCandidateList
      if (!commonList.length) {
        this.query.ram = ''
        return []
      }
      const _hardwareConfigCandidateList = deduplicateArrayByKey(
        commonList,
        [
          'ram',
          'gpuNum',
        ],
      )
      .filter((i) => (this.isGpuPlatform ? i.gpuNum === this.query.gpuNum : true))
      .sort(this.numSort('ram'))
      const res = _hardwareConfigCandidateList.map((item) => item.ram)
      if ((res.length === 1 || !commonList.some((i) => i.ram === this.query.ram)) && res.length) {
        this.query.ram = res[0]
      }
      return res
    },
    cpuList() {
      let temp = []
      if (this.hardwareConfigCandidates) {
        const list = this.isGpuPlatform && this.query.gpuNum
          ? this.hardwareConfigCandidates.filter(item => item.gpuNum === this.query.gpuNum)
          : this.hardwareConfigCandidates
        list.map(item => {
          if (!temp.includes(item.model)) temp.push(item.model)
        })
      }
      return temp
    },
    isCpuAndGpuEditable() {
      const tempArr = this.selectTaskList
      return tempArr.some(item => [25, 45].includes(item.status) || item.operable !== 1)
    },
    hardwareConfigCandidateGroupByModel() {
      return this.hardwareConfigCandidateList.reduce((res, cur) => {
        if (res[cur.model]) {
          res[cur.model].push(cur)
        } else {
          res[cur.model] = [cur]
        }
        return res
      }, {})
    },
    ramSupportModelTip() {
      const ramSupportModel = Array.from(
        new Set(this.hardwareConfigList.filter((i) => i.ram === this.query.ram).map((i) => i.model)),
      ).join('、')
      const model = this._isGpuPlatform ? 'GPU' : 'CPU'
      return this.$t('task_hardware_ram_tip', [this.query.ram, model, ramSupportModel])
    },
  },
  methods: {
    initHardwareConfig({
      hardwareConfigList,
      selectTaskList,
    }) {
      this.hardwareConfigList = hardwareConfigList
      this.selectTaskList = selectTaskList || []
      this.hardwareConfigCandidates = hardwareConfigList.filter(item => item.status === 1)
      const currentConfig = hardwareConfigList.filter(item => item.current).filter(item => item.status === 1)
      const { ram, gpuNum } = currentConfig.length ? currentConfig[0] : {}
      const model = currentConfig.map(i => i.model)
      const status = currentConfig.map(i => i.status)
      this.currentConfig = {
        model,
        ram,
        gpuNum,
        status,
      }
      this.query = {
        model,
        ram,
        gpuNum,
      }
      const isCurrentConfigWork = status.every(i => i === 1)
      if (!isCurrentConfigWork) {
        this.query.ram = ''
      }
    },
    gpuNumList() {
      let temp = []
      if (this.hardwareConfigList) {
        this.hardwareConfigList.map(item => {
          if (this.query.model.includes(item.model) && !temp.includes(item.gpuNum)) temp.push(item.gpuNum)
        })
      }
      this.query.gpuNum = temp[0]
      return temp
    },
    getCommonFiledConfigByModel(key) {
      const modelKeys = Object.keys(this.hardwareConfigCandidateGroupByModel)
      if (!modelKeys.length) return []
      const configList = this.hardwareConfigCandidateGroupByModel[modelKeys[0]]
      const commonList = configList.reduce((res, cur) => {
        if (
          modelKeys.every((item) => {
            if (item === modelKeys[0]) return true
            return this.hardwareConfigCandidateGroupByModel[item].some((i) => i[key] === cur[key])
          })
        ) {
          res.push(cur[key])
        }
        return res
      }, [])
      return this.hardwareConfigCandidateList.filter((i) => commonList.includes(i[key]))
    },
    numSort(property) {
      return function (a, b) {
        const regex = /\d+/
        if (!a[property] || !b[property]) return 0
        const matchA = a[property].match(regex)
        const matchB = b[property].match(regex)
        const numA = matchA ? matchA[0] : 0
        const numB = matchB ? matchB[0] : 1
        return numA - numB
      }
    },
    getSelectedHardwareId(
      config,
      queryData,
    ) {
      const { model, gpuNum, ram } = queryData
      if (!config.length || !model.length || !ram) return []
      if (this._isGpuPlatform && !gpuNum) return []
      return config
        .filter(
          (item) =>
            model.includes(item.model) &&
            (this._isGpuPlatform ? item.gpuNum === gpuNum : true) &&
            item.ram === ram,
        )
        .map((i) => i.id)
    },
  },
  watch: {
    'query.model'(val, oldVal) {
      if (!val.length) {
        this.query.ram = ''
        this.hardwareConfigIds = []
        return
      }
      this.hardwareConfigIds = this.getSelectedHardwareId(this.hardwareConfigCandidates, this.query)
    },
    'query.ram'(val) {
      if (!val) {
        this.hardwareConfigIds = []
        return
      }
      this.hardwareConfigIds = this.getSelectedHardwareId(this.hardwareConfigCandidates, this.query)
    },
    'query.gpuNum'(val) {
      if (!val && this._isGpuPlatform) {
        this.hardwareConfigIds = []
        return
      }
      this.hardwareConfigIds = this.getSelectedHardwareId(this.hardwareConfigCandidates, this.query)
    },
  },
}
